// Pure pagination math for the bloodwork-analysis PDF's "Markers Outside
// Optimal" (MOO) section.
//
// Each MOO column (highs on the left, lows on the right) is packed
// independently into "pages": each page holds one or more card slices
// stacked vertically without exceeding `pageCapacity`. Cards taller than
// a page get split between groups (and within a group, between markers)
// so each slice fits on one page. Continuation slices are tagged with
// `isContinuation: true` so the caller can append " (cont.)" to the
// title and so the section header above the page can be rendered as
// "Markers Outside Optimal (cont.)" on pages whose first slice is a
// continuation.
//
// Heights are caller-provided estimates (pixels). The packer is pure —
// no DOM measurement — which keeps it deterministic and testable. The
// constants are tuned in bloodwork-analysis-pdf.tsx with safety margin.
import { PanelMarker, Section } from '../models';

export type MooPanelGroup = {
  systemLabel: string;
  markers: PanelMarker[];
};

export type MooPanel = {
  key: string;
  title: string;
  sectionForZone: Section;
  groups: MooPanelGroup[];
};

export type MooPanelSlice = {
  key: string;
  origPanelKey: string;
  title: string;
  sectionForZone: Section;
  groups: MooPanelGroup[];
  isContinuation: boolean;
  // Total marker count across the WHOLE panel, not just this slice's
  // groups. The title shows the range's total (e.g. "Above Lab Range
  // (6)"), so a panel split across pages must show "(6)" on every slice,
  // not the per-page subset count.
  totalMarkerCount: number;
};

export type MooPaginationInput = {
  panels: MooPanel[];
  pageCapacity: number;
  cardOverhead: number;
  groupOverhead: number;
  markerHeight: number;
  columnGap: number;
};

export function packMooColumn(input: MooPaginationInput): MooPanelSlice[][] {
  const {
    panels,
    pageCapacity,
    cardOverhead,
    groupOverhead,
    markerHeight,
    columnGap,
  } = input;

  const pages: MooPanelSlice[][] = [[]];
  let pageUsed = 0;

  for (const panel of panels) {
    if (panel.groups.length === 0) continue;

    let pending: MooPanelGroup[] = panel.groups.map(g => ({
      systemLabel: g.systemLabel,
      markers: [...g.markers],
    }));
    const totalMarkerCount = panel.groups.reduce(
      (n, g) => n + g.markers.length, 0,
    );
    let isFirstSlice = true;

    while (pending.length > 0) {
      const gap = pages[pages.length - 1].length > 0 ? columnGap : 0;
      const remaining = pageCapacity - pageUsed - gap;
      const minSlice = cardOverhead + groupOverhead + markerHeight;

      if (remaining < minSlice) {
        pages.push([]);
        pageUsed = 0;
        continue;
      }

      let budget = remaining - cardOverhead;
      const sliceGroups: MooPanelGroup[] = [];

      while (pending.length > 0) {
        const group = pending[0];
        const wholeH = groupOverhead + group.markers.length * markerHeight;
        if (wholeH <= budget) {
          sliceGroups.push({ systemLabel: group.systemLabel, markers: [...group.markers] });
          budget -= wholeH;
          pending.shift();
        } else {
          const headerlessBudget = budget - groupOverhead;
          if (headerlessBudget < markerHeight) break;
          const canFit = Math.floor(headerlessBudget / markerHeight);
          sliceGroups.push({
            systemLabel: group.systemLabel,
            markers: group.markers.slice(0, canFit),
          });
          pending[0] = {
            systemLabel: group.systemLabel,
            markers: group.markers.slice(canFit),
          };
          break;
        }
      }

      if (sliceGroups.length === 0) {
        pages.push([]);
        pageUsed = 0;
        continue;
      }

      const sliceHeight =
        cardOverhead +
        sliceGroups.reduce(
          (h, g) => h + groupOverhead + g.markers.length * markerHeight,
          0,
        );

      pages[pages.length - 1].push({
        key: `${panel.key}_p${pages.length - 1}`,
        origPanelKey: panel.key,
        title: isFirstSlice ? panel.title : `${panel.title} (cont.)`,
        sectionForZone: panel.sectionForZone,
        groups: sliceGroups,
        isContinuation: !isFirstSlice,
        totalMarkerCount,
      });
      pageUsed += gap + sliceHeight;
      isFirstSlice = false;

      if (pending.length > 0) {
        pages.push([]);
        pageUsed = 0;
      }
    }
  }

  if (pages.length > 1 && pages[pages.length - 1].length === 0) {
    pages.pop();
  }

  return pages;
}
